import { useEffect, useState } from 'react';

import MillicastService from '@core/dashboard/components/HomeBottomBar/ShareScreen/helpers';

import { IUseMicrophoneTestProps, RenderFrameType } from './interfaces';

/**
 * Measures the live input level of the selected microphone for the test meter.
 *
 * Holds a `getUserMedia` stream and an `AudioContext` for as long as the step is
 * mounted, and releases both when it unmounts or the device changes.
 *
 * @param props.activeDeviceId - Microphone to open; changing it restarts the meter.
 * @param props.permissionState - Nothing is opened until this is `'granted'`.
 * @returns `volume`, the averaged frequency magnitude of the latest frame.
 */
export const useMicrophoneTest = ({
  activeDeviceId,
  permissionState,
}: IUseMicrophoneTestProps) => {
  const [volume, setVolume] = useState(0);

  useEffect(() => {
    // Local to this effect run, not a ref: on a device switch the cleanup of
    // the previous run must keep its own in-flight getUserMedia cancelled,
    // which a shared ref reset by the next run would not.
    let cancelled = false;
    let mediaStream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let frameId: number | null = null;

    const setMicrophoneValue: RenderFrameType = ({
      analyser,
      bufferLength,
    }) => {
      const dataArray = new Uint8Array(bufferLength);

      frameId = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }

        setMicrophoneValue({ analyser, bufferLength });
      });

      analyser.getByteFrequencyData(dataArray);

      // Compute the average volume from the frequency data
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

      // Scale the volume
      setVolume(Math.round(avg));
    };

    const startCheckMicro = async () => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: activeDeviceId }, noiseSuppression: true },
        video: false,
      });

      // The step can be left while getUserMedia is still in flight. Without
      // this the resolved stream keeps the microphone open for the rest of the
      // session with nothing left holding a reference to stop it (MV-1815).
      if (cancelled) {
        MillicastService.stopMediaStreamTracks(stream);
        return;
      }

      mediaStream = stream;

      const context = new AudioContext({ sampleRate: 44100 }); // To be compatible with more devices
      audioContext = context;

      const src = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();

      src.connect(analyser);
      analyser.fftSize = 256;

      setMicrophoneValue({
        analyser,
        bufferLength: analyser.frequencyBinCount,
      });
    };

    if (permissionState === 'granted') {
      startCheckMicro();
    }

    return () => {
      cancelled = true;

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      if (mediaStream) {
        MillicastService.stopMediaStreamTracks(mediaStream);
      }

      if (audioContext && audioContext.state !== 'closed') {
        // close() rejects if the context is already closing — nothing to do
        audioContext.close().catch(() => undefined);
      }
    };
  }, [activeDeviceId, permissionState]);

  return { volume };
};
